import type { SelectChangeEvent } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { Language } from 'config/enums';
import type { SelectItem } from 'features/ui/elements';
import { Select } from 'features/ui/elements';
import { AppConfigService } from 'features/app-config/services';
import { useAppSelector } from 'features/app/hooks';
import { callLanguageCodeSelector } from 'features/call/call-base/store/selectors';
import { isVriModeSelector } from 'features/multi-mode/store';

interface SelectLanguageProps {
  value: string;
  onChange: (event: SelectChangeEvent<Language>) => void;
  className?: string;
}

export const SelectLanguage: FC<SelectLanguageProps> = (props) => {
  let activeLanguages = AppConfigService.activeLanguages;
  const defaultLanguage = useAppSelector(callLanguageCodeSelector);
  const isVriMode = useAppSelector(isVriModeSelector);
  const isValidLanguage = activeLanguages.some(
    (language) => language.languageCode === props.value
  );
  const modifiedProps = isValidLanguage ? props : { ...props, value: '' };
  const { t } = useTranslation();
  if (isVriMode) {
    activeLanguages = activeLanguages.filter(
      (language) =>
        language.displayName.toLowerCase().includes('test') ||
        language.languageCode === defaultLanguage
    );
  }

  const options: SelectItem[] = activeLanguages.map((language) => ({
    value: language.languageCode,
    text: language.displayName,
  }));

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    props.onChange(event as SelectChangeEvent<Language>);
  };

  return (
    <Select
      {...modifiedProps}
      data-testid='select-language'
      placeholder={t('unspecified')}
      options={options}
      onChange={handleChange}
    />
  );
};
